// src/pages/HearingRepresentativePage.jsx
import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import HearingCalendar from '../components/HearingCalendar';
import Header from '../components/Header';
import { useAuth } from '../hooks/useAuth';
import { CalendarDays, FileText } from 'lucide-react'; 
import HearingRecordDetails from '../components/HearingRecordDetails';

// Imports required for the Table view
import RecordsTable from '../components/RecordsTable';
import Pagination from '../components/Pagination';
import {
  fetchCollectors,
  fetchRecords,
  selectHearingCollectors,
  selectHearingFilterCollector,
  selectHearingLimit,
  selectHearingLoading,
  selectHearingMessage,
  selectHearingPage,
  selectHearingRecords,
  selectHearingSearchQuery,
  selectHearingSelectedRecords,
  selectHearingTotalRecords,
  selectHearingViewingRecordId,
  selectHearingViewMode,
  setFilterCollector,
  setLimit,
  setPage,
  setSearchQuery,
  setSelectedRecords,
  setViewingRecordId,
  setViewMode,
  showMessage,
} from '../store/hearingSlice.js';

const HearingRepresentativePage = () => {
  const { user, logout } = useAuth();
  const dispatch = useDispatch();

  const viewMode = useSelector(selectHearingViewMode);
  const viewingRecordId = useSelector(selectHearingViewingRecordId);
  const message = useSelector(selectHearingMessage);
  const records = useSelector(selectHearingRecords);
  const collectors = useSelector(selectHearingCollectors);
  const loading = useSelector(selectHearingLoading);
  const searchQuery = useSelector(selectHearingSearchQuery);
  const page = useSelector(selectHearingPage);
  const limit = useSelector(selectHearingLimit);
  const totalRecords = useSelector(selectHearingTotalRecords);
  const filterCollector = useSelector(selectHearingFilterCollector);
  const selectedRecords = useSelector(selectHearingSelectedRecords);

  const navItems = [
    { label: 'Hearing Calendar', icon: <CalendarDays className="w-4 h-4" />, mode: 'calendar' },
    { label: 'All Patients', icon: <FileText className="w-4 h-4" />, mode: 'records' },
  ];

  const notify = useCallback(
    (text, type = 'error', duration = 5000) => {
      dispatch(showMessage(text, type, duration));
    },
    [dispatch]
  );

  const loadRecords = useCallback(
    () =>
      dispatch(fetchRecords())
        .unwrap()
        .catch((err) => notify(err?.message || 'Failed to fetch records.')),
    [dispatch, notify]
  );

  useEffect(() => {
    if (viewMode === 'records' && !viewingRecordId) {
      loadRecords();
    }
  }, [loadRecords, viewMode, viewingRecordId]);

  useEffect(() => {
    dispatch(setPage(1));
  }, [dispatch, filterCollector, searchQuery]);

  useEffect(() => {
    dispatch(fetchCollectors()).unwrap().catch((err) => {
      console.error('Failed to fetch collectors.', err);
      notify(err?.message || 'Could not fetch the list of collectors.');
    });
  }, [dispatch, notify]);

  // --- Handlers ---
  const handleViewDetails = (id) => {
    dispatch(setViewingRecordId(id));
  };

  const handleRecordSelect = (record) => {
    if (record && record._id) {
      dispatch(setViewingRecordId(record._id));
    }
  };

  const handleBackToView = () => {
    dispatch(setViewingRecordId(null));
  };

  const handleSelectionChange = (recordId) => {
    dispatch(
      setSelectedRecords(
        selectedRecords.includes(recordId)
          ? selectedRecords.filter((id) => id !== recordId)
          : [...selectedRecords, recordId]
      )
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      dispatch(setSelectedRecords(records.map((r) => r._id)));
    } else {
      dispatch(setSelectedRecords([]));
    }
  };

  // Prepare collectors list for the table (Logic from AdminPage)
  const collectorsForDropdown = [{ _id: 'unassigned', username: 'Unassigned' }, ...collectors];

  return (
    <>
      <Header
        user={user}
        onLogout={logout}
        navItems={navItems}
        currentViewMode={viewMode}
        onViewModeChange={(mode) => {
            dispatch(setViewMode(mode));
            dispatch(setViewingRecordId(null));
        }}
      />

      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 font-sans">
        
        {/* Message Alert Block (Added to match Admin Page) */}
        {message.text && (
            <div className="w-full px-4 sm:px-6 lg:px-8 pt-4">
                <div
                className={`p-4 rounded-xl border-l-4 ${
                    message.type === 'success'
                    ? 'bg-green-50 border-green-500 text-green-800'
                    : 'bg-red-50 border-red-500 text-red-800'
                }`}
                >
                <div className="flex items-center">
                    <div className="flex-shrink-0">
                    {message.type === 'success' ? (
                        <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                        </div>
                    ) : (
                        <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        </div>
                    )}
                    </div>
                    <div className="ml-3">
                    <p className="font-medium">{message.text}</p>
                    </div>
                </div>
                </div>
            </div>
        )}

        {/* 1. DETAILS VIEW */}
        {viewingRecordId ? (
          <HearingRecordDetails
            recordId={viewingRecordId}
            onBack={handleBackToView}
          />
        ) : (
          
          /* 2. MAIN VIEWS SWITCHER */
          <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
            
            {/* VIEW A: CALENDAR */}
            {viewMode === 'calendar' && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="rounded-lg overflow-hidden border border-gray-100 shadow-inner bg-white">
                  <HearingCalendar onViewDetails={handleViewDetails} />
                </div>
              </div>
            )}

            {/* VIEW B: RECORDS TABLE */}
            {viewMode === 'records' && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <RecordsTable
                  records={records}
                  collectors={collectorsForDropdown} // Passes the combined list
                  filterCollector={filterCollector}
                  setFilterCollector={(value) => dispatch(setFilterCollector(value))}
                  searchQuery={searchQuery}
                  setSearchQuery={(value) => dispatch(setSearchQuery(value))}
                  onRecordSelect={handleRecordSelect}
                  loading={loading}
                  selectedRecords={selectedRecords}
                  onSelectionChange={handleSelectionChange}
                  onSelectAll={handleSelectAll}
                  onDeleteSelected={() => {}} 
                  onReassign={() => {}}
                  userRole={user?.role}
                />

                <Pagination
                  page={page}
                  setPage={(value) => dispatch(setPage(value))}
                  limit={limit}
                  setLimit={(value) => dispatch(setLimit(value))}
                  total={totalRecords}
                />
              </div>
            )}

          </div>
        )}
      </div>
    </>
  );
};

export default HearingRepresentativePage;
